import { PREFIXO_GRUPO } from './bcfAgrupar'

// ---------------------------------------------------------------------------
// As etiquetas de um tópico BCF divididas por tipo (facetas).
//
// Existe por causa de uma queixa concreta (15/10, Cha139 com 1191 pins):
// com as etiquetas em E, escolher "MAC" e "HID" dava zero tópicos, porque
// nenhum conflito tem as duas especialidades ao mesmo tempo, e o painel
// mostrava "0 de 1191 pins" sem dizer porquê. O que se quer ao escolher
// várias especialidades é "os de MAC ou os de HID"; ao juntar um nível,
// "desses, os do P2". Isso pede OU dentro de cada tipo de etiqueta e E
// entre tipos, e por isso as etiquetas dividem-se aqui em facetas.
//
// A divisão é uma heurística sobre o texto da etiqueta, não uma lista
// fechada: o BCF não tem campo para a especialidade nem para o nível
// (são etiquetas livres), e os ficheiros vêm de várias plataformas. A
// regra, por esta ordem:
//
//   1. Grupo: começa por "Grupo " (a etiqueta da exportação dos
//      conflitos, `PREFIXO_GRUPO`). Os pins tratam-na à parte (o critério
//      Grupo, que também lê os RelatedTopics).
//   2. Nível: começa por "Nível ", "Nivel ", "Piso ", "Level " ou
//      "Storey ", sem distinguir maiúsculas ("Nível P2", "PISO 3",
//      "Level 01").
//   3. Especialidade: o rótulo inteiro tem de 2 a 4 letras maiúsculas,
//      sem algarismos nem espaços (ARQ, EST, MAC, HID, EEL, MEP, AVAC).
//      É a forma dos códigos de especialidade da exportação dos conflitos
//      (`etiquetasDoTopico` em viewer/clash/clashBcf.ts) e da maioria das
//      plataformas. Uma sigla que não seja especialidade (por exemplo
//      "BIM") cai aqui também: é o preço de não ter lista fechada, e não
//      tira resultados (só muda a faceta em que se escolhe).
//   4. Outras: o resto ("Conflito", "Atravessamento", "Urgente"). OU entre
//      si, como nas outras facetas.
//
// Um BCF sem especialidades nem níveis (outra plataforma, etiquetas
// livres) fica todo em "Outras" e comporta-se como uma faceta só, em OU.
// ---------------------------------------------------------------------------

export type FacetaDaEtiqueta = 'grupo' | 'especialidade' | 'nivel' | 'outras'

/** As facetas pela ordem em que aparecem nos painéis. */
export const FACETAS_DAS_ETIQUETAS: readonly FacetaDaEtiqueta[] = ['especialidade', 'nivel', 'outras', 'grupo']

export const ROTULO_DA_FACETA: Record<FacetaDaEtiqueta, string> = {
  grupo: 'Grupo',
  especialidade: 'Especialidade',
  nivel: 'Nível',
  outras: 'Outras etiquetas',
}

const RE_NIVEL = /^(n[ií]vel|piso|level|storey)\s/iu
const RE_ESPECIALIDADE = /^\p{Lu}{2,4}$/u

/** A faceta de uma etiqueta (ver a regra no topo do ficheiro). */
export function facetaDaEtiqueta(etiqueta: string): FacetaDaEtiqueta {
  if (etiqueta.startsWith(PREFIXO_GRUPO)) return 'grupo'
  const t = etiqueta.trim()
  if (RE_NIVEL.test(t)) return 'nivel'
  if (RE_ESPECIALIDADE.test(t)) return 'especialidade'
  return 'outras'
}

/** As etiquetas escolhidas, separadas por faceta. Só aparecem as facetas
 *  com alguma etiqueta. */
export function etiquetasPorFaceta(etiquetas: Iterable<string>): Map<FacetaDaEtiqueta, Set<string>> {
  const porFaceta = new Map<FacetaDaEtiqueta, Set<string>>()
  for (const e of etiquetas) {
    const f = facetaDaEtiqueta(e)
    let s = porFaceta.get(f)
    if (!s) porFaceta.set(f, (s = new Set()))
    s.add(e)
  }
  return porFaceta
}

// As etiquetas escolhidas mudam pouco e o filtro corre uma vez por
// tópico (milhares por render): a divisão fica guardada por conjunto.
const cache = new WeakMap<ReadonlySet<string>, Map<FacetaDaEtiqueta, Set<string>>>()

/** O tópico passa as etiquetas escolhidas? Dentro de cada faceta basta
 *  uma (OU); entre facetas, todas (E). Sem nenhuma escolhida, passa. */
export function etiquetasPassam(etiquetasDoTopico: readonly string[], escolhidas: ReadonlySet<string>): boolean {
  if (escolhidas.size === 0) return true
  let porFaceta = cache.get(escolhidas)
  if (!porFaceta) {
    porFaceta = etiquetasPorFaceta(escolhidas)
    cache.set(escolhidas, porFaceta)
  }
  for (const s of porFaceta.values()) {
    if (!etiquetasDoTopico.some((l) => s.has(l))) return false
  }
  return true
}
